import React from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, DollarSign, MapPin, Rocket, Star, Heart, Zap } from 'lucide-react';
import Tilt from 'react-parallax-tilt';

const Careers: React.FC = () => {
  const benefits = [
    {
      icon: Users,
      title: 'Great Team',
      description: 'Work with passionate professionals who support each other',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Clock,
      title: 'Flexible Hours',
      description: 'Work-life balance with flexible scheduling and remote options',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: DollarSign,
      title: 'Competitive Pay',
      description: 'Competitive salaries and comprehensive benefits package',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: MapPin,
      title: 'Growth Opportunities',
      description: 'Continuous learning and career advancement opportunities',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const values = [
    {
      icon: Rocket,
      title: 'Innovation',
      description: 'Be part of cutting-edge technology projects'
    },
    {
      icon: Star,
      title: 'Excellence',
      description: 'Strive for excellence in everything we do'
    },
    {
      icon: Heart,
      title: 'Collaboration',
      description: 'Work together to achieve amazing results'
    },
    {
      icon: Zap,
      title: 'Impact',
      description: 'Make a real difference in the tech world'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-primary-400/20 to-purple-600/20 dark:from-cyan-400/10 dark:to-purple-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-72 h-72 bg-gradient-to-tr from-cyan-400/20 to-blue-600/20 dark:from-cyan-400/10 dark:to-blue-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-gray-900 via-primary-600 to-purple-600 dark:from-white dark:via-cyan-400 dark:to-purple-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              Join Our Team
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Be part of an innovative team that's shaping the future of technology. 
              We offer exciting opportunities for growth and impact.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Why Work With Lunexa?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              We believe in creating an environment where talent thrives
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Tilt
                    tiltMaxAngleX={10}
                    tiltMaxAngleY={10}
                    perspective={1000}
                    scale={1.02}
                  >
                    <div className="group text-center h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                      <motion.div
                        className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${benefit.gradient} mb-6 shadow-lg`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </motion.div>
                      <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </Tilt>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Tilt
              tiltMaxAngleX={5}
              tiltMaxAngleY={5}
              perspective={1000}
              scale={1.02}
            >
              <div className="relative backdrop-blur-xl bg-gradient-to-br from-white/20 to-white/10 dark:from-gray-800/20 dark:to-gray-800/10 border border-white/30 dark:border-gray-700/30 rounded-3xl p-16 shadow-glass max-w-4xl mx-auto">
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-purple-500/10 dark:from-cyan-500/10 dark:to-purple-500/10 rounded-3xl" />
                
                <div className="relative z-10">
                  <motion.div
                    className="inline-flex p-6 rounded-3xl bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-500 dark:to-purple-500 mb-8 shadow-2xl"
                    animate={{ 
                      scale: [1, 1.05, 1],
                      rotate: [0, 2, -2, 0]
                    }}
                    transition={{ 
                      duration: 4, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <Rocket className="w-16 h-16 text-white" />
                  </motion.div>

                  <motion.h2
                    className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 bg-clip-text text-transparent"
                    animate={{ 
                      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    We Arrive Soon!
                  </motion.h2>
                  
                  <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                    Our career opportunities are launching soon. Get ready to join an amazing team 
                    that's building the future of technology.
                  </p>

                  <motion.div
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-500 dark:to-purple-500 text-white font-semibold rounded-2xl shadow-lg"
                    animate={{ 
                      boxShadow: [
                        "0 0 20px rgba(59, 130, 246, 0.3)",
                        "0 0 40px rgba(59, 130, 246, 0.6)",
                        "0 0 20px rgba(59, 130, 246, 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <span className="text-lg">Stay Tuned for Exciting Opportunities</span>
                  </motion.div>
                </div>
              </div>
            </Tilt>
          </motion.div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/5 via-purple-600/5 to-cyan-600/5 dark:from-primary-600/3 dark:via-purple-600/3 dark:to-cyan-600/3" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              What We Value
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              The principles that make our team extraordinary
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Tilt
                    tiltMaxAngleX={10}
                    tiltMaxAngleY={10}
                    perspective={1000}
                    scale={1.02}
                  >
                    <div className="group text-center h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                      <motion.div
                        className="inline-flex p-4 rounded-2xl bg-gradient-to-r from-primary-500 to-purple-500 dark:from-cyan-500 dark:to-purple-500 mb-6 shadow-lg"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </motion.div>
                      <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </Tilt>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact for Interest */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Tilt
              tiltMaxAngleX={5}
              tiltMaxAngleY={5}
              perspective={1000}
              scale={1.02}
            >
              <div className="backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-12 shadow-glass">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 bg-clip-text text-transparent">
                  Interested in Joining Us?
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                  While we're preparing our career opportunities, we'd love to hear from talented individuals 
                  who are passionate about technology and innovation.
                </p>
                
                <motion.div
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <motion.a
                    href="/contact"
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-500 dark:to-purple-500 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Get In Touch</span>
                  </motion.a>
                  
                  <motion.a
                    href="mailto:support@lunexa.dpdns.org"
                    className="inline-flex items-center px-8 py-4 backdrop-blur-xl bg-white/20 dark:bg-gray-800/20 border border-white/30 dark:border-gray-700/30 text-gray-900 dark:text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>support@lunexa.dpdns.org</span>
                  </motion.a>
                </motion.div>
              </div>
            </Tilt>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;